import { GridColDef } from "@mui/x-data-grid";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import DialogActions from "src/containers/DialogActions/DialogActions";
import FilterByDays from "src/containers/FilterByDays/FilterByDays";
import useChangeDays from "src/containers/FilterByDays/useChangeDays/useChangeDays";
import LoadingOverlay from "src/containers/LoadingOverlay/LoadingOverlay";
import {
  AnomalyTypesQuickfilter,
  useAnomalyTypesQuickFilter,
} from "src/containers/Quickfilters/AnomalyTypesQuickfilter";

import QuickfiltersContainer from "src/containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { CustomDate } from "src/models/custom-date/custom-date";
import { EmployeeRef } from "src/models/employees";
import { AnomalyTrackingDTO } from "src/models/tna/tna-anomalies/anomaly-tracking-dto";
import { AnomalyTrackingFilter } from "src/models/tna/tna-anomalies/anomaly-tracking-filter";
import Table from "src/reusable-components/Table/Table";
import { AnomaliesTrackingByEmployees } from "src/services/tna-anomalies/anomalies-tracking-by-employees-api.service";
import { AnomalyTrackingByEmployee } from "src/services/tna-anomalies/anomaly-tracking-by-employee-api.service";
import { FT_TNAHT } from "src/utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "src/utils/time-utils";
import { v4 as uuidv4 } from "uuid";

interface Props {
  employee?: EmployeeRef | null;
  handleNotifyParent: () => void;
  tableHeight: number;
  handleClose: () => void;
  days?: CustomDate[];
}

export interface State {
  loading: boolean;
  error: string;
  employee: EmployeeRef | null;
  lastNotifiedOn: Date | null;
  lastTimesheetSignOff: Date | null;
  allEmployees: boolean;
  employeeQfId: string;
  showResolvedAnomalies: boolean;
}

const SingleEmployeeAnomaliesHistoryTable = ({
  employee,
  handleNotifyParent,
  tableHeight,
  handleClose,
  days,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_team_breaks.anomalies_history_table"; // tRP - translation root path

  const { anomalyTypesQuickFilter, setAnomalyTypesQuickFilter, allAnomalyTypes, anomalyTypeQFIsLoading } =
    useAnomalyTypesQuickFilter(true);

  const { daysQuickFilter, setDaysQuickFilter, selectedDays } = useChangeDays();

  // const hasAccess = useAuthorization([
  //   AUTHORIZATIONS.EmployeesAccess.CreateEmployees,
  //   AUTHORIZATIONS.EmployeesAccess.ConfigureEmployeeGroupParameters,
  //   AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
  // ]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    employee: employee || null,
    lastNotifiedOn: null,
    lastTimesheetSignOff: null,
    allEmployees: true,
    employeeQfId: uuidv4(),
    showResolvedAnomalies: true,
  });

  // const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  // const selectedRowsDataRef = useRef<AnomalyTrackingDTO[]>([]);

  const columns: GridColDef<AnomalyTrackingDTO>[] = [
    {
      field: "lastNotifiedOn",
      headerName: t("common_words.last_notified_on"),
      headerAlign: "center",
      sortable: true,
      disableColumnMenu: true,
      valueGetter: ({ row }) => (row.lastNotifiedOn ? intlFormatDateShort(new Date(row.lastNotifiedOn)) : ""),
      flex: 1,
      align: "center",
    },
    {
      field: "anomalyDescription",
      headerName: t("common_words.anomaly"),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) => row?.anomalyType?.description ?? "",
      flex: 1.5,
      align: "left",
    },
    {
      field: "anomalyDate",
      headerName: t(`${tRP}.fields.date_of_anomaly`),
      headerAlign: "center",
      sortable: true,
      disableColumnMenu: true,
      valueGetter: ({ row }) => intlFormatDateShort(new Date(row?.year, row?.month - 1, row?.day)) ?? "",
      flex: 1,
      align: "center",
    },
    {
      field: "comment",
      headerName: t(`${tRP}.comment_notification`),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        // <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        //   <Typography
        //     variant="body2"
        //     style={{ flex: 1 }}
        //   >
        //     {row?.comment ?? ""}
        //   </Typography>
        //   {(!row?.comment || row?.comment === "") && (
        //     <AddEditComment
        //       item={row}
        //       effect={row?.comment ? "edit" : "add"}
        //       // handleNotifyParent={forceUpdate}
        //       disabled={!hasAccess || allowedToViewOnly}
        //       viewMode={allowedToViewOnly}
        //     >
        //       <IconButton
        //         size="small"
        //         title="Edit comment"
        //       >
        //         <FontAwesomeIcon
        //           icon={faPenToSquare}
        //           color={theme.palette.primary.contrastText}
        //         />
        //       </IconButton>
        //     </AddEditComment>
        //   )}
        // </div>
        row?.comment ?? "",
      flex: 3,
      align: "left",
    },
  ];

  const quickFilters = React.useMemo(
    () => (
      <QuickfiltersContainer>
        <AnomalyTypesQuickfilter
          quickFilters={anomalyTypesQuickFilter}
          handleChangeQuickFilters={setAnomalyTypesQuickFilter}
          allAnomalyTypesOption
          allAnomalyTypes={allAnomalyTypes}
          isMultiSelect
        />
        {days?.length === 0 && (
          <FilterByDays
            quickFilters={daysQuickFilter}
            handleChangeQuickFilters={setDaysQuickFilter}
            filterColor="filter"
            withMaxDate={false}
            maxSelectableDays={31}
          />
        )}
        {/* </Grid> */}
      </QuickfiltersContainer>
    ),
    [allAnomalyTypes, anomalyTypesQuickFilter, days, daysQuickFilter, setAnomalyTypesQuickFilter, setDaysQuickFilter]
  );

  const propsFilter = React.useMemo(
    () =>
      ({
        employeesIds: [state.employee?.id],
        isResolved: state.showResolvedAnomalies,
        days: days?.length !== 0 ? days : selectedDays,
        typeIds: anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds || [],
        isNotified: true,
        isAbnormalPresence: false,
      } as AnomalyTrackingFilter),
    [
      state.employee?.id,
      state.showResolvedAnomalies,
      days,
      selectedDays,
      anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds,
    ]
  );

  return (
    <LoadingOverlay loading={state.loading || anomalyTypeQFIsLoading}>
      {/* {employee?.id && (
        <Grid
          container
          sx={{ alignItems: "center" }}
          spacing={2}
        >
          <Grid
            item
            xs={6}
          >
            <ConcatenatedCell
              aligned={"left"}
              FirstElement={
                <Grid>
                  <Typography>{employee?.name}</Typography>
                  {employee?.birthDate && (
                    <Typography variant={"body2"}>
                      <FontAwesomeIcon
                        icon={faBirthdayCake}
                        size="xs"
                      />{" "}
                      &nbsp;
                      {intlFormatDateShort(employee?.birthDate)}
                    </Typography>
                  )}
                </Grid>
              }
              SecondElement={
                <Grid>
                  <Typography
                    style={{ color: theme.palette.text.disabled }}
                    variant={"body2"}
                    title={employee?.legalEntity?.name}
                  >
                    {employee?.legalEntity?.code ?? employee?.legalEntity?.name}
                  </Typography>
                </Grid>
              }
            />
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ textAlign: "right" }}
          >
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              Last anomaly notification sent on:{" "}
              {state.lastNotifiedOn ? intlFormatDateShort(state.lastNotifiedOn) : "N/A"}
            </Typography>
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              Last signoff date: {state.lastTimesheetSignOff ? intlFormatDateShort(state.lastTimesheetSignOff) : "N/A"}
            </Typography>
          </Grid>
        </Grid>
      )} */}
      <Table
        hideRemoveIcon
        translationRootPath={tRP}
        displayTitle={false}
        columns={columns}
        allowEdit={false}
        readOnly
        defaultPageSize={10}
        tableHeight={tableHeight}
        serviceApi={!employee?.id ? new AnomaliesTrackingByEmployees() : new AnomalyTrackingByEmployee()}
        // getAllItems={handleGetAllItems}
        propsFilter={propsFilter}
        quickFilters={quickFilters}
        filter={FT_TNAHT}
      />
      <DialogActions
        handleCancel={() => {
          // selectedRowsDataRef.current = [];
          setState((prev) => ({ ...prev, error: "" }));
          handleClose();
        }}
        // disabledSaveButton
        handleSave={handleClose}
        // disabledSaveButton={state.loading}
        disabledCancelButton={state.loading}
        customSaveName={t(`${tRP}.close`)}
      />
    </LoadingOverlay>
  );
};

export default React.memo(SingleEmployeeAnomaliesHistoryTable);
